/**
 * MarginBreakdownTooltip — the hover readout for one category.
 *
 * It carries the TRUE gross margin, not the plotted one. A far-outlier GM% is clamped to
 * the edge of the scale so it cannot flatten every other point; the number itself is never
 * altered, and this is where the real figure is stated. An unpriced child says so rather
 * than reporting a zero it does not have.
 */

import { COST_COMPONENTS } from '../../../viewmodels/orderBook'
import { kLabel, type BreakdownRow } from './marginBreakdownScale'

function Line({ name, value }: { name: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-mut">{name}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </div>
  )
}

export default function MarginBreakdownTooltip({ active, payload }: {
  active?: boolean
  payload?: { payload?: BreakdownRow }[]
}) {
  const row = active ? payload?.[0]?.payload : undefined
  if (!row) return null
  const { bar } = row
  return (
    <div className="rounded-lg border border-line bg-panel px-2.5 py-2 text-[11px] leading-[1.6] text-ink shadow-cx">
      <div className="mb-1 font-mono text-[10px] text-mut2">{row.label}</div>
      <Line name="Value" value={kLabel(bar.value)} />
      {bar.cost === null ? (
        <div className="text-mut2">
          {bar.offLens ? `Costed on the ${bar.offLens} lens — not on this one` : 'No cost basis'}
        </div>
      ) : (
        <>
          <Line name="Cost" value={kLabel(bar.cost)} />
          <Line name="Margin" value={kLabel(bar.margin ?? 0)} />
          <Line
            name="GM%"
            value={`${row.gmTrue?.toFixed(1) ?? '—'}%${row.clip ? ` · off the scale (${row.clip})` : ''}`}
          />
          {bar.components && (
            <div className="mt-1 border-t border-line2 pt-1 font-mono text-[10px] text-mut">
              {COST_COMPONENTS.filter((n) => (bar.components?.[n] ?? 0) > 0)
                .map((n) => `${n} ${kLabel(bar.components?.[n] ?? 0)}`)
                .join(' · ')}
            </div>
          )}
        </>
      )}
    </div>
  )
}
